/**
 * Step definitions for the interactive walkthrough/tutorial.
 * Keys: "controller-action" (e.g. "projects-index", "home-index").
 * Steps use Driver.js format: { element?: string|function, popover: { title, description } }.
 * No repeated nav/breadcrumb steps per page; current nav is highlighted by the controller.
 */
export const walkthroughStepsByPage = {
  "projects-index": [
    {
      element: "main",
      popover: {
        title: "Projects",
        description:
          "Here you manage projects and their 360° product views. Create a project, then add products and upload images.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: ".tour-new-project-link",
      popover: {
        title: "Create a project",
        description:
          "Click \"New Project\" to create a project. Give it a name—you can add products and upload images after.",
        side: "bottom",
        align: "end",
      },
    },
    {
      element: "#projects_list",
      popover: {
        title: "Projects list",
        description:
          "Your projects appear here. Click a row to open a project and add products. If you don't have any yet, use \"New Project\" first.",
        side: "top",
        align: "start",
      },
    },
    {
      element: "#navbar-actions",
      popover: {
        title: "Quick actions",
        description:
          "Info: summary. Filter: search projects. New Project: create a project. Open a project to upload and customize products.",
        side: "bottom",
        align: "end",
      },
    },
    {
      element: "#projects_list",
      popover: {
        title: "Next: Open project",
        description:
          "Click Next to open your first project and see how to add and customize products. If you don't have a project yet, click Next again to go to Account.",
        side: "top",
        align: "start",
        nextBtnText: "Next →",
      },
      navigateToFirstProject: true,
    },
    {
      element: ".tour-projects-link",
      popover: {
        title: "Next: Account",
        description:
          "Click Next to go to your Account page to manage plan, billing, and settings.",
        side: "bottom",
        align: "start",
        nextBtnText: "Next →",
      },
      navigateTo: "/account",
      nextTourPage: "account-index",
    },
  ],

  "projects-show": [
    {
      element: "main",
      popover: {
        title: "Project",
        description:
          "This project contains your products (360° views). Add a product by uploading images, then customize spin speed and appearance.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: "#variants_list",
      popover: {
        title: "Products",
        description:
          "Products listed here. Each product is a 360° view. Click a row to open it, or use Upload to add a new product.",
        side: "top",
        align: "start",
      },
    },
    {
      element: ".tour-upload-link",
      popover: {
        title: "Add a product",
        description:
          "Click \"Upload\" to add a new product. Upload 4–360 images or a video; the app builds the 360° spin. You can then customize it.",
        side: "bottom",
        align: "end",
      },
    },
    {
      element: "#navbar-actions",
      popover: {
        title: "Project actions",
        description:
          "Info, Filter, Upload, Share, and More (rename, domains, watermark). Open a product row to customize its spin and embed it.",
        side: "bottom",
        align: "end",
      },
    },
    {
      element: ".tour-upload-link",
      popover: {
        title: "Next: Upload",
        description:
          "Click Next to go to the Upload page and add a new product (360° view) to this project.",
        side: "bottom",
        align: "end",
        nextBtnText: "Next →",
      },
      navigateToUpload: true,
    },
  ],

  "home-index": [
    {
      element: "main",
      popover: {
        title: "Dashboard",
        description:
          "Overview of your account. Use Quick Stats and Quick Actions, or go to Projects to create and manage 360° product views.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: () => {
        const icon = document.querySelector(".card-header h3 i.bi-bar-chart");
        return icon ? icon.closest(".card") : null;
      },
      popover: {
        title: "Quick Stats",
        description:
          "See how many projects and products you have. Create a project and add products if you're just getting started.",
        side: "top",
        align: "start",
      },
    },
    {
      element: () => {
        const icon = document.querySelector(".card-header h3 i.bi-lightning");
        return icon ? icon.closest(".card") : null;
      },
      popover: {
        title: "Quick Actions",
        description:
          "New Project, View All Projects, Search Products. Go to Projects to create a project, then upload and customize products.",
        side: "top",
        align: "end",
      },
    },
    {
      element: "#variants_list",
      popover: {
        title: "Recent products",
        description:
          "Your most recent products. Open one to customize or embed. If the list is empty, go to Projects and add a product.",
        side: "top",
        align: "start",
      },
    },
    {
      element: ".tour-projects-link",
      popover: {
        title: "Next: Projects",
        description:
          "Click Next to go to the Projects page to create a project and add products.",
        side: "bottom",
        align: "start",
        nextBtnText: "Next →",
      },
      navigateTo: "/projects",
      nextTourPage: "projects-index",
    },
  ],

  "account-index": [
    {
      element: "main",
      popover: {
        title: "Account",
        description:
          "Manage your plan, billing, usage, and account settings. Use the links below to jump to each section.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: () => {
        const h3 = document.querySelector(".card-header h3 i.bi-house");
        return h3 ? h3.closest(".card") : document.querySelector(".card");
      },
      popover: {
        title: "Account sections",
        description:
          "Overview, Plan, Billing, Usage, Settings, 2FA, Logo, Watermark, and more. Explore each section as needed.",
        side: "top",
        align: "start",
      },
    },
    {
      element: ".tour-account-link",
      popover: {
        title: "Tour complete",
        description:
          "You've seen Dashboard, Projects, and Account. Use \"Take a tour\" in the top nav anytime to run this again.",
        side: "bottom",
        align: "start",
      },
    },
  ],

  "variants-show": [
    {
      element: "main",
      popover: {
        title: "Product",
        description:
          "This is a 360° product view. Customize spin speed and appearance, then embed or share it.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: () => document.querySelector("[data-controller*='preview-spin']") || document.querySelector(".card"),
      popover: {
        title: "Preview",
        description:
          "Preview the 360° spin here. Use the Customize tab to change speed, background, and other options.",
        side: "top",
        align: "start",
      },
    },
    {
      element: "#navbar-actions",
      popover: {
        title: "Product actions",
        description:
          "Info, Re-upload, Customize, Embed, Export, Download, and more. Customize to adjust spin and appearance before embedding.",
        side: "bottom",
        align: "end",
      },
    },
  ],

  "variants-customize": [
    {
      element: "main",
      popover: {
        title: "Customize",
        description:
          "Adjust spin speed, background, starting frame, and other options. Changes update the preview; then embed or share the product.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: () => document.querySelector(".card") || document.querySelector("form"),
      popover: {
        title: "Customization options",
        description:
          "Set auto-spin speed, manual spin, background color, zoom, and more. Save when done; your embed code will reflect the new settings.",
        side: "top",
        align: "start",
      },
    },
  ],

  "projects-upload": [
    {
      element: "main",
      popover: {
        title: "Upload",
        description:
          "Add a new product: upload 4–360 JPG/PNG images or a video. Choose single row, multi-row, or gallery. Then create the 360° view.",
        side: "bottom",
        align: "center",
      },
    },
    {
      element: () => {
        const upload = document.querySelector("#normal_upload, #multirow_upload, #video_upload, #gallery_upload");
        return (upload && upload.closest(".card")) || document.querySelector(".card");
      },
      popover: {
        title: "Create 360° view",
        description:
          "Upload images or video, then click \"Create 360° view\". After processing, open the product to customize and embed.",
        side: "top",
        align: "start",
      },
    },
    {
      element: "#breadcrumbs",
      popover: {
        title: "Next: Back to project",
        description:
          "Click Next to go back to the project and see your products. You can run the tour on a product page to see Customize and Embed.",
        side: "bottom",
        align: "start",
        nextBtnText: "Next →",
      },
      navigateToProjectUrl: true,
    },
  ],
};

/**
 * @param {string} pageKey - e.g. "projects-index"
 * @returns {Array|null} steps for that page or null if none
 */
export function getStepsForPage(pageKey) {
  return walkthroughStepsByPage[pageKey] || null;
}
